import * as React from 'react';
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormHelperText,
  FormLabel,
  Input,
  Box,
  IconButton,
  InputAdornment,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import { customerSchema } from 'schema';
import * as yup from 'yup';
import { postApi, getApi } from 'views/services/api';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import moment from 'moment';

const AgregarVisitante = ({ open, handleClose, reservationId }) => {
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Obtener datos del hotel desde localStorage
  const hotelData = React.useMemo(() => {
    const data = localStorage.getItem('hotelData');
    return data ? JSON.parse(data) : null;
  }, []);

  // Validación personalizada para hacer opcionales teléfono y email
  const visitorSchema = customerSchema.shape({
    phoneNumber: yup.string().notRequired(),
    email: yup.string().email('Formato de correo inválido').notRequired()
  });

  // Valores iniciales del formulario
  const initialValues = {
    phoneNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    idCardType: 'default',
    idcardNumber: '',
    address: '',
    idFile: null,
    visitTime: ''
  };

  // Funciones de búsqueda
  const fetchCustomerData = async (phoneNumber) => {
    try {
      setIsSearching(true);
      const response = await getApi(`api/customer/view/${phoneNumber}?hotelId=${hotelData?.hotelId}`);
      setIsSearching(false);
      return response?.data?.customerData[0];
    } catch (error) {
      console.log(error);
      setIsSearching(false);
      toast.error('No se pudieron obtener los datos del visitante');
    }
  };

  // Función para agregar visitante
  const agregarVisitante = async (values, resetForm) => {
    if (!hotelData?._id) {
      toast.error('No se encontró información del hotel');
      return;
    }

    setLoading(true);

    try {
      const horaActual = moment().format('h:mm A');

      const formData = new FormData();
      formData.append('firstName', values.firstName);
      formData.append('lastName', values.lastName);
      formData.append('phoneNumber', values.phoneNumber || '');
      formData.append('hotelId', hotelData._id);
      formData.append('email', values.email || '');
      formData.append('idCardType', values.idCardType);
      formData.append('idcardNumber', values.idcardNumber);
      formData.append('address', values.address);
      formData.append('visitTime', horaActual);
      if (reservationId) {
        formData.append('reservationId', reservationId);
      }

      if (values.idFile) {
        formData.append('idFile', values.idFile);
      }

      const response = await postApi('api/visitors/add', formData);

      if (response.status === 200) {
        toast.success('Visitante agregado exitosamente');
        resetForm();
        handleClose();
      } else {
        toast.error(response?.response?.data?.error || 'Error al agregar visitante');
      }
    } catch (error) {
      console.error('Error al agregar visitante:', error);
      toast.error(error?.response?.data?.error || 'Error al agregar visitante');
    } finally {
      setLoading(false);
    }
  };

  // Configuración de Formik
  const formik = useFormik({
    initialValues,
    validationSchema: visitorSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      await agregarVisitante(values, resetForm);
    }
  });

  // Manejador para el input de número de teléfono
  const handlePhoneChange = (e) => {
    const phoneNumber = e.target.value.replace(/\D/g, '');
    const truncatedPhoneNumber = phoneNumber.substring(0, 10);
    formik.setFieldValue('phoneNumber', truncatedPhoneNumber);
    formik.setFieldValue('idFile', '');
  };

  // Manejador para el archivo
  const handleFileChange = (event) => {
    const file = event.currentTarget.files?.[0];
    if (file) {
      formik.setFieldValue('idFile', file);
    }
  };

  // Manejador para cerrar el diálogo
  const handleDialogClose = () => {
    formik.resetForm();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth aria-labelledby="agregar-visitante-titulo">
      <DialogTitle id="agregar-visitante-titulo">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Agregar Visitante</Typography>
          <IconButton edge="end" color="inherit" onClick={handleDialogClose} aria-label="cerrar" disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box component="form" onSubmit={formik.handleSubmit} noValidate>
          <Grid container spacing={3}>
            {/* Número de Teléfono */}
            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary', display: 'block' }}>
                <PhoneIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                Número de Teléfono
              </FormLabel>
              <TextField
                id="phoneNumber"
                name="phoneNumber"
                size="small"
                type="tel"
                fullWidth
                placeholder="Ingrese 10 dígitos"
                value={formik.values.phoneNumber}
                onChange={handlePhoneChange}
                error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                disabled={loading}
                inputProps={{ maxLength: 10 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Buscar visitante existente">
                        <span>
                          <IconButton
                            edge="end"
                            disabled={!formik.values.phoneNumber || formik.values.phoneNumber.length < 10 || isSearching || loading}
                            onClick={async () => {
                              if (formik.values.phoneNumber.length === 10) {
                                const data = await fetchCustomerData(formik.values.phoneNumber);
                                if (data) {
                                  formik.setValues({
                                    ...formik.values,
                                    phoneNumber: formik.values.phoneNumber,
                                    firstName: data.firstName || '',
                                    lastName: data.lastName || '',
                                    email: data.email || '',
                                    idCardType: data.idCardType || 'default',
                                    idcardNumber: data.idcardNumber || '',
                                    address: data.address || '',
                                    idFile: data.idFile || null
                                  });
                                  toast.success('✅ Visitante encontrado');
                                } else {
                                  toast.info('ℹ️ Visitante nuevo - Complete los datos');
                                }
                              }
                            }}
                            color="primary"
                          >
                            {isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {/* Número de Identificación */}
            <Grid item xs={12} sm={6}>
              <FormLabel required sx={{ mb: 1, fontWeight: 600, color: 'text.primary', display: 'block' }}>
                <BadgeIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                Número de Identificación
              </FormLabel>
              <TextField
                id="idcardNumber"
                name="idcardNumber"
                size="small"
                type="text"
                fullWidth
                placeholder="V-12345678 o pasaporte"
                value={formik.values.idcardNumber}
                onChange={formik.handleChange}
                error={formik.touched.idcardNumber && Boolean(formik.errors.idcardNumber)}
                helperText={formik.touched.idcardNumber && formik.errors.idcardNumber}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Buscar por identificación">
                        <span>
                          <IconButton
                            edge="end"
                            disabled={!formik.values.idcardNumber || formik.values.idcardNumber.length < 5 || isSearching || loading}
                            onClick={async () => {
                              const idCardNumber = formik.values.idcardNumber;
                              if (idCardNumber.length >= 5) {
                                setIsSearching(true);
                                try {
                                  const response = await getApi(`api/customer/viewById/${idCardNumber}?hotelId=${hotelData?.hotelId}`);
                                  const customerData = response?.data?.customerData[0];

                                  if (customerData) {
                                    formik.setValues({
                                      ...formik.values,
                                      phoneNumber: customerData.phoneNumber || '',
                                      firstName: customerData.firstName || '',
                                      lastName: customerData.lastName || '',
                                      email: customerData.email || '',
                                      idCardType: customerData.idCardType || 'default',
                                      idcardNumber: formik.values.idcardNumber,
                                      address: customerData.address || '',
                                      idFile: customerData.idFile || null
                                    });

                                    if (customerData.isExternal) {
                                      toast.success('✅ Datos encontrados en registro civil');
                                    } else {
                                      toast.success('✅ Visitante encontrado');
                                    }
                                  } else {
                                    toast.info('ℹ️ Visitante nuevo');
                                  }
                                } catch (error) {
                                  toast.info('ℹ️ Visitante nuevo');
                                } finally {
                                  setIsSearching(false);
                                }
                              }
                            }}
                            color="primary"
                          >
                            {isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {/* Nombre */}
            <Grid item xs={12} sm={6}>
              <FormLabel required sx={{ mb: 1, fontWeight: 600, color: 'text.primary', display: 'block' }}>
                <PersonIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                Nombre
              </FormLabel>
              <TextField
                id="firstName"
                name="firstName"
                size="small"
                fullWidth
                placeholder="Ingrese nombre"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                disabled={loading}
              />
            </Grid>

            {/* Apellido */}
            <Grid item xs={12} sm={6}>
              <FormLabel required sx={{ mb: 1, fontWeight: 600, color: 'text.primary', display: 'block' }}>
                Apellido
              </FormLabel>
              <TextField
                id="lastName"
                name="lastName"
                size="small"
                fullWidth
                placeholder="Ingrese apellido"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                disabled={loading}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary', display: 'block' }}>
                <EmailIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                Correo Electrónico
              </FormLabel>
              <TextField
                id="email"
                name="email"
                type="email"
                size="small"
                fullWidth
                placeholder="Ingrese correo electrónico"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled={loading}
              />
            </Grid>

            {/* Tipo de Identificación */}
            <Grid item xs={12} sm={6}>
              <FormLabel required sx={{ mb: 1, fontWeight: 600, color: 'text.primary', display: 'block' }}>
                Tipo de Identificación
              </FormLabel>
              <Select
                id="idCardType"
                name="idCardType"
                size="small"
                fullWidth
                value={formik.values.idCardType}
                onChange={formik.handleChange}
                error={formik.touched.idCardType && Boolean(formik.errors.idCardType)}
                disabled={loading}
              >
                <MenuItem value="default" disabled>
                  Seleccione tipo de identificación
                </MenuItem>
                <MenuItem value="ID">Cédula de Identidad</MenuItem>
                <MenuItem value="Passport">Pasaporte</MenuItem>
                <MenuItem value="Rif">RIF</MenuItem>
                <MenuItem value="DrivingLicense">Licencia de Conducir</MenuItem>
              </Select>
              {formik.touched.idCardType && formik.errors.idCardType && <FormHelperText error>{formik.errors.idCardType}</FormHelperText>}
            </Grid>

            {/* Dirección */}
            <Grid item xs={12}>
              <FormLabel required sx={{ mb: 1, fontWeight: 600, color: 'text.primary', display: 'block' }}>
                <HomeIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                Dirección
              </FormLabel>
              <TextField
                id="address"
                name="address"
                size="small"
                fullWidth
                placeholder="Ingrese dirección física"
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                disabled={loading}
              />
            </Grid>

            {/* Archivo de Identificación */}
            <Grid item xs={12}>
              <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary', display: 'block' }}>
                Cargar Archivo de Identificación
              </FormLabel>
              <Input
                id="idFile"
                name="idFile"
                type="file"
                fullWidth
                onChange={handleFileChange}
                disabled={loading}
                inputProps={{
                  accept: 'image/*,.pdf'
                }}
              />
              {formik.values.idFile && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Archivo seleccionado: {formik.values.idFile.name || 'Archivo Existente en DB'}
                </Typography>
              )}
            </Grid>
          </Grid>

          <DialogActions sx={{ mt: 3, px: 0 }}>
            <Button onClick={handleDialogClose} disabled={loading} color="inherit">
              Cancelar
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={loading || !formik.isValid}>
              {loading ? 'Agregando...' : 'Agregar Visitante'}
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

AgregarVisitante.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  reservationId: PropTypes.string
};

export default AgregarVisitante;
